import { useState, useCallback } from 'react'

interface CompanyProps {
    kedo: any
    settings: any
}

interface PaginationState {
    currentPage: number
    resultatenPerPagina: number
    totalResults: number
    totalPages: number
    isLoading: boolean
    hasNextPage: boolean
    hasPreviousPage: boolean
}

const useCompany = () => {
    const [companies, setCompanies] = useState<Record<string, any>>([])
    const [pagination, setPagination] = useState<PaginationState>({
        currentPage: 1,
        resultatenPerPagina: 10,
        totalResults: 0,
        totalPages: 0,
        isLoading: false,
        hasNextPage: false,
        hasPreviousPage: false,
    })
    const endpoint = '/kvk/search'

    const searchCompanies = useCallback(
        async ({
            kedo,
            settings,
            page = 1,
            resultatenPerPagina = 10,
        }: CompanyProps & { page?: number; resultatenPerPagina?: number }) => {
            if (!settings) {
                return { companies: [], pagination: pagination }
            }

            setPagination((prev) => ({ ...prev, isLoading: true }))

            const searchParams = {
                ...settings,
                pagina: page.toString(),
                resultatenperpagina: resultatenPerPagina.toString(),
            }

            const params = new URLSearchParams(searchParams).toString()

            try {
                const response = await kedo
                    .api()
                    .post(`${endpoint}?${params}`, {
                        environment_id: kedo.getCurrentEnvironmentId(),
                    })

                if (response.data?.resultaten) {
                    let results = response.data.resultaten

                    results = results.map((result, idx) => {
                        let enhancedResult = { ...result }

                        if (result.adres?.binnenlandsAdres) {
                            enhancedResult = {
                                ...enhancedResult,
                                straatnaam:
                                    result.adres.binnenlandsAdres.straatnaam ||
                                    '',
                                huisnummer:
                                    result.adres.binnenlandsAdres.huisnummer ||
                                    '',
                                postcode:
                                    result.adres.binnenlandsAdres.postcode ||
                                    '',
                                plaats:
                                    result.adres.binnenlandsAdres.plaats || '',
                                land: result.adres.binnenlandsAdres.land || '',
                                volledigAdres:
                                    result.adres.binnenlandsAdres
                                        .volledigAdres || '',
                            }
                        }

                        if (
                            response.data.extra?._embedded?.hoofdvestiging
                                ?.adressen?.[idx]
                        ) {
                            enhancedResult = {
                                ...enhancedResult,
                                ...response.data.extra._embedded.hoofdvestiging
                                    .adressen[idx],
                            }
                        }

                        if (response.data.extra) {
                            enhancedResult = {
                                ...enhancedResult,
                                totaalWerkzamePersonen:
                                    response.data.extra.totaalWerkzamePersonen,
                                formeleRegistratiedatum:
                                    response.data.extra.formeleRegistratiedatum,
                                materieleRegistratie:
                                    response.data.extra.materieleRegistratie,
                                handelsnamen: response.data.extra.handelsnamen,
                                sbiActiviteiten:
                                    response.data.extra.sbiActiviteiten,
                                eigenaar: response.data.extra.eigenaar,
                            }
                        }

                        return enhancedResult
                    })

                    setCompanies(results)

                    const newPagination: PaginationState = {
                        currentPage: response.data.pagina || page,
                        resultatenPerPagina:
                            response.data.resultatenPerPagina ||
                            resultatenPerPagina,
                        totalResults: response.data.totaal || 0,
                        totalPages: Math.ceil(
                            (response.data.totaal || 0) /
                                (response.data.resultatenPerPagina ||
                                    resultatenPerPagina)
                        ),
                        isLoading: false,
                        hasNextPage: !!(
                            response.data.links?.find(
                                (link: any) => link.rel === 'next'
                            ) || response.data.volgende
                        ),
                        hasPreviousPage: page > 1,
                    }

                    setPagination(newPagination)

                    return { companies: results, pagination: newPagination }
                } else {
                    setCompanies([])
                    setPagination((prev) => ({
                        ...prev,
                        isLoading: false,
                        totalResults: 0,
                        totalPages: 0,
                        hasNextPage: false,
                        hasPreviousPage: false,
                    }))
                    return {
                        companies: [],
                        pagination: { ...pagination, isLoading: false },
                    }
                }
            } catch (error) {
                console.error('Company search error:', error)
                setCompanies([])
                setPagination((prev) => ({
                    ...prev,
                    isLoading: false,
                    totalResults: 0,
                    totalPages: 0,
                    hasNextPage: false,
                    hasPreviousPage: false,
                }))
                return {
                    companies: [],
                    pagination: { ...pagination, isLoading: false },
                }
            }
        },
        [pagination]
    )

    const autocomplete = useCallback(
        ({ kedo, settings }: CompanyProps) => {
            searchCompanies({ kedo, settings })
        },
        [searchCompanies]
    )

    const goToPage = useCallback(
        async (page: number, kedo: any, currentSettings: any) => {
            return await searchCompanies({
                kedo,
                settings: currentSettings,
                page,
                resultatenPerPagina: pagination.resultatenPerPagina,
            })
        },
        [searchCompanies, pagination.resultatenPerPagina]
    )

    const nextPage = useCallback(
        async (kedo: any, currentSettings: any) => {
            if (pagination.hasNextPage && !pagination.isLoading) {
                return await goToPage(
                    pagination.currentPage + 1,
                    kedo,
                    currentSettings
                )
            }
            return { companies, pagination }
        },
        [pagination, goToPage, companies]
    )

    const previousPage = useCallback(
        async (kedo: any, currentSettings: any) => {
            if (pagination.hasPreviousPage && !pagination.isLoading) {
                return await goToPage(
                    pagination.currentPage - 1,
                    kedo,
                    currentSettings
                )
            }
            return { companies, pagination }
        },
        [pagination, goToPage, companies]
    )

    const firstPage = useCallback(
        async (kedo: any, currentSettings: any) => {
            if (!pagination.isLoading) {
                return await goToPage(1, kedo, currentSettings)
            }
            return { companies, pagination }
        },
        [pagination, goToPage, companies]
    )

    const lastPage = useCallback(
        async (kedo: any, currentSettings: any) => {
            if (!pagination.isLoading) {
                return await goToPage(
                    pagination.totalPages,
                    kedo,
                    currentSettings
                )
            }
            return { companies, pagination }
        },
        [pagination, goToPage, companies]
    )

    const changePageSize = useCallback(
        async (newPageSize: number, kedo: any, currentSettings: any) => {
            setPagination((prev) => ({
                ...prev,
                resultatenPerPagina: newPageSize,
            }))
            return await searchCompanies({
                kedo,
                settings: currentSettings,
                page: 1,
                resultatenPerPagina: newPageSize,
            })
        },
        [searchCompanies]
    )

    const resetPagination = useCallback(() => {
        setPagination({
            currentPage: 1,
            resultatenPerPagina: 10,
            totalResults: 0,
            totalPages: 0,
            isLoading: false,
            hasNextPage: false,
            hasPreviousPage: false,
        })
        setCompanies([])
    }, [])

    return {
        companies,
        pagination,
        autocomplete,
        searchCompanies,
        goToPage,
        nextPage,
        previousPage,
        firstPage,
        lastPage,
        changePageSize,
        resetPagination,
    }
}

export default useCompany
